import { Gem, ShoppingBag, Briefcase, PartyPopper, Clock, Search, ArrowDown, MessageSquare, FileCheck, Truck } from "lucide-react";
import { FadeUp, Eyebrow, MaskedLine } from "@/components/Reveal";
import Marquee from "@/components/Marquee";
import ConciergeForm from "@/components/forms/ConciergeForm";
import { CONTACT } from "@/data/products";

const SERVICES = [
  { icon: Gem, title: "Luxury & High-Value Gifts", desc: "Milestone pieces and significant gestures, sourced with care and complete discretion." },
  { icon: ShoppingBag, title: "Personal Shopping", desc: "A dedicated shopper for the things you don't have time — or inclination — to hunt down." },
  { icon: Briefcase, title: "Executive & Corporate Gifting", desc: "Board-level gifts, client gestures and team recognition that reflect well on your house." },
  { icon: PartyPopper, title: "Event & Party Gifts", desc: "Goodie bags, table favours and VIP gifting — from ten guests to five hundred." },
  { icon: Clock, title: "Last-Minute Gifting", desc: "When tomorrow is the deadline, urgency is our brief. We move quickly and quietly." },
  { icon: Search, title: "Rare & Hard-to-Find", desc: "Sold-out, discontinued or simply elusive — we explore legitimate sourcing routes worldwide." },
];

const PROCESS_STEPS = [
  { icon: MessageSquare, n: "01", title: "Brief us privately", text: "Use the form below, email or call — a few honest lines about what you need and when is enough." },
  { icon: Search, n: "02", title: "We scope it honestly", text: "We explore legitimate sourcing options, availability and timescales. If it can't be done properly, we tell you — plainly and quickly." },
  { icon: FileCheck, n: "03", title: "Proposal agreed", text: "A transparent quote covering the item, our service and delivery. Nothing is ever paid before you approve it." },
  { icon: Truck, n: "04", title: "We handle everything", text: "Sourcing, quality checks, signature wrapping and tracked delivery — with confirmation to you at every step." },
];

const SCENARIOS = [
  { title: "The CEO's dilemma", text: "An important gift for the board chair — needed tomorrow morning, and it cannot look last-minute. We source, wrap and courier it overnight." },
  { title: "Fifty goodie bags", text: "A private event in one week and fifty luxury goodie bags to fill. One brief to us; one flawless delivery to the venue." },
  { title: "The sold-out piece", text: "The one thing they truly wanted, sold out everywhere. We trace legitimate availability and give you an honest answer on timescales." },
];

export default function Concierge() {
  return (
    <div data-testid="concierge-page" className="bg-espresso text-cream">
      <section className="relative pt-40 pb-20 lg:pb-28 spotlight-dark overflow-hidden">
        <div className="px-4 sm:px-8 lg:px-16 max-w-7xl mx-auto">
          <FadeUp><Eyebrow dark>Private Shopping &amp; Gifting Concierge</Eyebrow></FadeUp>
          <h1 className="font-serif font-light text-4xl sm:text-5xl lg:text-6xl leading-[1.08] tracking-tight mt-6 text-cream max-w-4xl">
            <MaskedLine delay={0.1}>You tell us what you need.</MaskedLine>
            <MaskedLine delay={0.28} className="italic text-champagne">We handle the rest.</MaskedLine>
          </h1>
          <FadeUp delay={0.4}>
            <p className="text-base md:text-lg text-cream/60 leading-relaxed max-w-2xl mt-7">
              A discreet, personal service for gifts and sourcing that are urgent, high-value or simply hard to find. Brief us privately — a member of our concierge team, never a queue, will reply.
            </p>
            <div className="flex flex-wrap gap-4 mt-10">
              <a href="#concierge-form" data-testid="concierge-hero-cta"
                className="group inline-flex items-center gap-2 bg-gold text-espresso px-8 py-4 font-mono text-[11px] uppercase tracking-[0.2em] hover:bg-champagne transition-colors duration-300">
                Make an Enquiry <ArrowDown size={14} className="group-hover:translate-y-0.5 transition-transform" />
              </a>
              <a href={`mailto:${CONTACT.conciergeEmail}`} data-testid="concierge-email-button"
                className="inline-flex items-center gap-2 border border-champagne/40 text-champagne px-8 py-4 font-mono text-[11px] uppercase tracking-[0.2em] hover:bg-white/5 transition-colors duration-300">
                Email the Concierge
              </a>
            </div>
          </FadeUp>
        </div>
      </section>

      <Marquee dark />

      {/* WHAT IT IS */}
      <section className="py-20 lg:py-28 px-4 sm:px-8 lg:px-16 max-w-7xl mx-auto">
        <div className="grid lg:grid-cols-12 gap-12">
          <div className="lg:col-span-5">
            <FadeUp>
              <Eyebrow dark>What the concierge is</Eyebrow>
              <h2 className="font-serif text-2xl sm:text-3xl lg:text-4xl font-light text-cream mt-5 leading-[1.18]">
                A private desk for the things that <span className="italic text-champagne">can't wait — or can't be found.</span>
              </h2>
            </FadeUp>
          </div>
          <div className="lg:col-span-7">
            <FadeUp delay={0.1}>
              <div className="space-y-6 text-sm text-cream/60 leading-relaxed">
                <p className="font-serif text-xl lg:text-2xl text-cream/85 font-light leading-snug">
                  Think of us as your personal gifting office: one call, one message, and the whole matter is quietly taken off your desk.
                </p>
                <p>
                  It's used by chief executives and their assistants, private offices, event planners, and anyone whose diary doesn't leave room for the thoughtful gesture they still want to make. You bring the brief; we bring the network, the legwork and the finish.
                </p>
                <p>
                  Every client gets a single dedicated point of contact, access to our sourcing network, quality checks on every item, our signature wrapping, and delivery logistics handled end to end. For corporate clients, consolidated invoicing is available.
                </p>
              </div>
            </FadeUp>
            <FadeUp delay={0.2}>
              <ul className="divide-y divide-white/10 border-y border-white/10 mt-10">
                {[
                  "Urgent briefs answered within hours",
                  "Standard briefs answered the same day",
                  "One dedicated contact, start to finish",
                  "Same-day courier available in London",
                ].map((t) => (
                  <li key={t} className="py-4 flex items-center gap-4">
                    <span className="w-1.5 h-1.5 rotate-45 bg-gold shrink-0" />
                    <span className="font-serif text-lg text-cream/85 font-light">{t}</span>
                  </li>
                ))}
              </ul>
            </FadeUp>
          </div>
        </div>
      </section>

      {/* SCENARIOS */}
      <section className="py-20 lg:py-28 px-4 sm:px-8 lg:px-16 max-w-7xl mx-auto">
        <FadeUp>
          <Eyebrow dark>The kind of calls we take</Eyebrow>
          <h2 className="font-serif text-2xl sm:text-3xl lg:text-4xl font-light text-cream mt-5 leading-[1.15]">
            Impossible timelines, <span className="italic text-champagne">quietly met.</span>
          </h2>
        </FadeUp>
        <div className="grid md:grid-cols-3 gap-6 mt-14">
          {SCENARIOS.map((s, i) => (
            <FadeUp key={s.title} delay={i * 0.12} className="h-full">
              <div data-testid={`concierge-scenario-${i + 1}`} className="h-full border border-white/10 hover:border-gold/40 p-8 transition-colors duration-500 bg-white/[0.02]">
                <span className="font-serif text-4xl font-light text-gold/50">{String(i + 1).padStart(2, "0")}</span>
                <h3 className="font-serif text-2xl font-light text-cream mt-5">{s.title}</h3>
                <p className="text-sm text-cream/55 leading-relaxed mt-4">{s.text}</p>
              </div>
            </FadeUp>
          ))}
        </div>
      </section>

      {/* SERVICES */}
      <section className="py-20 lg:py-28 border-y border-white/10 bg-white/[0.02]">
        <div className="px-4 sm:px-8 lg:px-16 max-w-7xl mx-auto">
          <FadeUp>
            <Eyebrow dark>What we handle</Eyebrow>
            <h2 className="font-serif text-2xl sm:text-3xl lg:text-4xl font-light text-cream mt-5 leading-[1.15]">
              One brief. <span className="italic text-champagne">Every detail covered.</span>
            </h2>
          </FadeUp>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-px bg-white/10 border border-white/10 mt-14">
            {SERVICES.map((s, i) => (
              <FadeUp key={s.title} delay={i * 0.08} className="h-full">
                <div data-testid={`concierge-service-${i + 1}`} className="group h-full bg-espresso p-8 lg:p-10 hover:bg-[#241610] transition-colors duration-500">
                  <s.icon size={24} strokeWidth={1.3} className="text-champagne" />
                  <h3 className="font-serif text-xl text-cream font-light mt-6">{s.title}</h3>
                  <p className="text-sm text-cream/50 leading-relaxed mt-3">{s.desc}</p>
                </div>
              </FadeUp>
            ))}
          </div>
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section className="py-20 lg:py-28 px-4 sm:px-8 lg:px-16 max-w-7xl mx-auto">
        <FadeUp>
          <Eyebrow dark>How it works</Eyebrow>
          <h2 className="font-serif text-2xl sm:text-3xl lg:text-4xl font-light text-cream mt-5 leading-[1.15]">
            From private brief <span className="italic text-champagne">to perfectly handled.</span>
          </h2>
        </FadeUp>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-10 mt-16">
          {PROCESS_STEPS.map((s, i) => (
            <FadeUp key={s.n} delay={i * 0.1}>
              <div data-testid={`concierge-process-${i + 1}`} className="border-t border-gold/40 pt-7">
                <s.icon size={22} strokeWidth={1.3} className="text-champagne" />
                <span className="block font-serif text-4xl font-light text-gold/50 mt-5">{s.n}</span>
                <h3 className="font-serif text-xl text-cream font-light mt-3">{s.title}</h3>
                <p className="text-sm text-cream/50 leading-relaxed mt-3">{s.text}</p>
              </div>
            </FadeUp>
          ))}
        </div>
        <FadeUp delay={0.2} className="mt-14 text-center">
          <a href="#concierge-form" data-testid="concierge-process-cta"
            className="group inline-flex items-center gap-2 bg-gold text-espresso px-8 py-4 font-mono text-[11px] uppercase tracking-[0.2em] hover:bg-champagne transition-colors duration-300">
            Start Your Private Enquiry <ArrowDown size={14} className="group-hover:translate-y-0.5 transition-transform" />
          </a>
        </FadeUp>
      </section>

      {/* DISCRETION / HONESTY */}
      <section className="py-20 lg:py-28 px-4 sm:px-8 lg:px-16 max-w-7xl mx-auto">
        <div className="grid lg:grid-cols-2 gap-12 items-start">
          <FadeUp>
            <Eyebrow dark>Our word</Eyebrow>
            <h2 className="font-serif text-2xl sm:text-3xl lg:text-4xl font-light text-cream mt-5 leading-[1.2]">
              Discreet always. <span className="italic text-champagne">Honest first.</span>
            </h2>
          </FadeUp>
          <FadeUp delay={0.15}>
            <div className="space-y-6 text-sm text-cream/60 leading-relaxed">
              <p>
                We will explore legitimate sourcing options, availability and timescales on your behalf — and we will always tell you plainly what is possible. We never guarantee the availability of luxury or rare items, and we never take payment before a proposal is agreed.
              </p>
              <p>
                Every enquiry is handled in confidence. Client names, recipients and occasions are never discussed, shared or published.
              </p>
              <ul className="space-y-3 pt-2">
                {["Transparent proposals before any commitment", "Full sourcing notes on request", "Confidential by default"].map((t) => (
                  <li key={t} className="flex items-center gap-3 text-cream/75">
                    <span className="w-1.5 h-1.5 rotate-45 bg-gold shrink-0" /> {t}
                  </li>
                ))}
              </ul>
            </div>
          </FadeUp>
        </div>
      </section>

      {/* FORM */}
      <section id="concierge-form" className="pb-24 lg:pb-36 px-4 sm:px-8 lg:px-16 max-w-7xl mx-auto scroll-mt-24">
        <div className="bg-ivory text-chocolate p-6 sm:p-10 lg:p-14 grid lg:grid-cols-12 gap-10 frame-gold">
          <div className="lg:col-span-4">
            <FadeUp>
              <Eyebrow>Private enquiry</Eyebrow>
              <h2 className="font-serif text-2xl sm:text-3xl font-light text-chocolate mt-5 leading-[1.2]">
                Brief us <span className="italic text-golddark">privately.</span>
              </h2>
              <p className="text-sm text-chocolate/60 leading-relaxed mt-5">
                A few lines are enough. Your enquiry goes directly to our concierge team, who will reply personally — usually within hours for urgent requests.
              </p>
              <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-golddark mt-8">
                Strictly confidential · No obligation
              </p>
            </FadeUp>
          </div>
          <div className="lg:col-span-8">
            <FadeUp delay={0.15}>
              <ConciergeForm />
            </FadeUp>
          </div>
        </div>
      </section>
    </div>
  );
}
